/**
 * @file Login.tsx
 * @description Component for user login, integrating with Firebase Authentication.
 */

import React, { useState } from "react";
import GoogleAuthButton from "./GoogleAuthButton";
import log from "../../config/logging";
import { useUserDetails } from "../../context/UserDetailsContext";

interface LoginProps {
  onSuccess: () => void;
  switchToSignUp: () => void;
}

const Login: React.FC<LoginProps> = ({ onSuccess, switchToSignUp }) => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const { signInWithEmailPassword } = useUserDetails();

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    log.debug("Login started", { email });
    setError("");
    try {
      await signInWithEmailPassword(email, password);
      onSuccess();
    } catch (error) {
      log.error("Login error", { error });
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchToSignUp = (e: React.MouseEvent) => {
    e.preventDefault();
    switchToSignUp();
  };

  return (
    <>
      <form onSubmit={handleLogin} className="flex flex-col">
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full mb-4 text-center normal-case border border-gray-300 rounded-full bg-base-100 input text-primary sm:min-w-60"
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="w-full mb-4 text-center normal-case border border-gray-300 rounded-full bg-base-100 input text-primary sm:min-w-60"
        />
        <button
          type="submit"
          className="px-6 mb-4 text-lg font-bold border-none rounded-full text-formbutton text-primary-content bg-primary btn"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="loading loading-spinner"></span> Accesso in
              corso…
            </>
          ) : (
            "Accedi"
          )}
        </button>
        <p className="my-4 text-center text-neutral">
          Non hai un account?{" "}
          <button
            type="button"
            className="underline text-primary"
            onClick={handleSwitchToSignUp}
          >
            Registrati
          </button>
        </p>
        <div className="mb-8 uppercase divider text-neutral">Oppure</div>
        <GoogleAuthButton onSuccess={onSuccess} />
        {error && <p className="mt-4 text-center text-error">{error}</p>}
      </form>
    </>
  );
};

export default Login;
